import { Injectable, inject, signal } from "@angular/core";
import { TemporaryArea } from "@models/map/market-filter.model";
import { IGroupsWithMetaData, PolyDrawHelpers, PolyDrawService, PolygonInformationService } from "polydraw";
import { Observable, catchError, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { AreaStructure } from "src/models/area-builder/area-structure.model";
import { ExtendedAreaViewModel } from "src/models/area-builder/area.model";
import { QueryArea } from "src/models/area-builder/query-area.model";
import { ILatLng, TemporaryAreas } from "src/models/area-builder/temporary-areas.model";
import { IAreaBoundary } from "src/models/map/areaboundary.model";
import { AreasStore } from "src/stores/area-builder.store";
import { AreaBuilderUtils } from "src/util/map/areabuilder.util";
import { HttpDataService } from "./httpdata.service";
import { MapActionsService } from "./map/map-actions.service";
import { SearchDataService } from "./search-data.service";

interface AreaId {
    id: string;
}

@Injectable({
    providedIn: "root",
})
export class AreaBuilderDataService {
    readonly areasStore = inject(AreasStore);
    readonly http = inject(HttpDataService);
    private readonly areaBuilderUtils = inject(AreaBuilderUtils);
    private readonly mapActionsService = inject(MapActionsService);
    private readonly polygonInfoService = inject(PolygonInformationService);
    private readonly mapHelperService = inject(PolyDrawService);
    private readonly dictionary = new Map<string, IGroupsWithMetaData>();

    private polyHelper: PolyDrawHelpers;

    /**
     *
     */
    constructor() {
        this.polyHelper = new PolyDrawHelpers(this.polygonInfoService);
    }

    getAreaStructure(queryArea: QueryArea): Observable<AreaStructure> {
        const previousLoadedStructure = this.areasStore.getLoadedStructure(queryArea.areaId, queryArea.areaType);
        if (previousLoadedStructure) return of(previousLoadedStructure);
        return this.http.post<{ data: AreaStructure }>(`${environment.apiUrl}/Map/QueryAreaStructure`, queryArea).pipe(
            map((payload) => payload.data),
            tap((structure) => this.areasStore.addLoadedStructure(queryArea.areaId, queryArea.areaType, structure)),
        );
    }

    saveTemporaryAreas(): Observable<string> {
        // dont store drawn polygons in areas, we fetch it again from the map
        const areas = this.areasStore.selectedAreas().map((area) => ({ ...area, geographicBorder: [] as any[] }));
        const drawnPolygons = this.areasStore.selectedDrawnPolygons().reduce((prev, current) => prev.concat(current?.layer), []);
        return this.http
            .post<{ data: AreaId }>(`${environment.apiUrl}/Map/SaveTemporaryAreas`, { areas, drawnPolygons })
            .pipe(map((payload) => payload.data.id));
    }

    deleteTemporaryAreas(temporaryStorageId: string): Observable<string> {
        return this.http
            .delete<{ data: AreaId }>(`${environment.apiUrl}/Map/TemporaryAreas/${temporaryStorageId}`)
            .pipe(map((payload) => payload.body.data.id));
    }

    getTemporaryAreas(areaBuilderId: string): Observable<TemporaryAreas> {
        return this.http.get<{ data: TemporaryAreas }>(`${environment.apiUrl}/Map/TemporaryAreas/${areaBuilderId}`).pipe(map((payload) => payload.data));
    }

    getAreaBoundaryCached(area: ExtendedAreaViewModel): Observable<IGroupsWithMetaData> {
        const cacheId = `${area.areaId}-${area.areaType}`;
        if (area.geographicBorder) {
            this.dictionary.set(cacheId, this.mapToGroupWithMetaData(area));
        }
        if (this.dictionary.has(cacheId)) {
            return of(this.dictionary.get(cacheId));
        } else {
            return this.getAreaBoundary(area.areaId, area.areaType).pipe(
                map((boundary) => {
                    const mapped = this.mapToGroupWithMetaData({ ...area, geographicBorder: boundary?.geographicBorder });
                    this.dictionary.set(cacheId, mapped);
                    return mapped;
                }),
            );
        }
    }

    private mapToGroupWithMetaData(area: ExtendedAreaViewModel): IGroupsWithMetaData {
        return {
            metaData: {
                customName: area.name,
                customId: area.number,
                areaId: area.areaId,
                areaTypeId: area.areaType,
                draggable: false,
            },
            layer: area.geographicBorder as L.LatLng[][][],
            styles: { color: "#3d91c6", fillColor: "#3d91c6" },
        };
    }

    getAreaBoundary(areaId: number, areaType: number): Observable<IAreaBoundary> {
        return this.http.get<{ data: any }>(`${environment.apiUrl}/Map/AreaBoundary/${areaType}/${areaId}`).pipe(
            catchError(() => of({ data: {} })),
            map((payload) => payload.data),
        );
    }

    readonly areasAndPolygons = signal({
        areas: [] as TemporaryArea[],
        selfDrawnPolygons: [] as ILatLng[][][],
    });

    getAreasAndPolygons() {
        return this.areasAndPolygons();
    }

    setAreasAndPolygons(withGeoBorderForAreas = false) {
        const layers = this.polyHelper.getAllLayersWithMetaDataFromMap(this.mapActionsService.map);
        const selfDrawnPolygons = this.areaBuilderUtils.getDrawnPolygonsFromLayers(layers);
        const areas = this.areaBuilderUtils.getAreasFromLayers(layers, withGeoBorderForAreas);
        this.areasAndPolygons.set({ areas, selfDrawnPolygons });
        return { areas, selfDrawnPolygons };
    }

    // Clears everything on the map and redraws the given areas/polygons, without re-fitting the map bounds.
    // Use this instead of deletePolygon() when removing a single area/polygon: deletePolygon() leaves the
    // polygon's menu marker (".special-polygon-marker") orphaned on the map, whereas a full redraw drops it.
    redrawAreasAndPolygons(areas: TemporaryArea[], geoBorder: ILatLng[][][]): void {
        this.mapHelperService.removeAllFeatureGroups();
        const featureGroups = this.areaBuilderUtils.getFeatureGroups(geoBorder, areas);
        if (featureGroups.length) {
            this.mapHelperService.addAllPolygonsWithMetaData(featureGroups);
        }
    }

    positionMapToAreasAndPolygons(areas: TemporaryArea[], geoBorder: ILatLng[][][]): void {
        const featureGroups = this.areaBuilderUtils.getFeatureGroups(geoBorder, areas);
        this.mapHelperService.addAllPolygonsWithMetaData(featureGroups);
        const bBox = this.polyHelper.getCombinedBoundingBox();
        this.mapActionsService.setBounds(bBox.getSouth(), bBox.getNorth(), bBox.getWest(), bBox.getEast(), true, {
            paddingTopLeft: [150, 100],
            paddingBottomRight: [100, 150],
        });
    }
}
